import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, Pill, ReportPage, StatGrid, dateTimeLabel, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface CustomerRow {
  customer: string
  phone: string
  bills: number
  outstanding: number
  oldestDays: number
  buckets: number[]
}
interface OpenBill {
  _id: string
  billNumber: string
  customer: string
  phone: string
  time: string | null
  days: number
  amount: number
}
interface ReceivablesData {
  customers: CustomerRow[]
  bills: OpenBill[]
  summary: { totalOutstanding: number; customerCount: number; openBills: number; over30: number; buckets: number[] }
}

const BUCKET_LABELS = ['0–7 days', '8–15 days', '16–30 days', '30+ days']
const ageTone = (days: number) => (days > 30 ? 'bad' : days > 15 ? 'warn' : 'muted')

export default function ReceivablesScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<ReceivablesData>('receivables', { dateBound: false })
  const data = report.data

  const customerColumns: Column<CustomerRow>[] = [
    { key: 'customer', header: 'Customer', render: r => <span className="font-medium">{r.customer}</span> },
    { key: 'phone', header: 'Phone', render: r => r.phone || '—' },
    { key: 'bills', header: 'Open bills', align: 'right' },
    ...BUCKET_LABELS.map<Column<CustomerRow>>((label, i) => ({
      key: `b${i}`,
      header: label,
      align: 'right',
      render: r => (r.buckets[i] ? formatCurrency(r.buckets[i]) : '—'),
      value: r => r.buckets[i]
    })),
    { key: 'outstanding', header: 'Outstanding', align: 'right', render: r => <span className="font-semibold">{formatCurrency(r.outstanding)}</span> }
  ]
  const billColumns: Column<OpenBill>[] = [
    { key: 'billNumber', header: 'Bill', render: r => <span className="font-medium">#{r.billNumber}</span> },
    { key: 'customer', header: 'Customer' },
    { key: 'time', header: 'Billed on', render: r => dateTimeLabel(r.time) },
    { key: 'days', header: 'Pending', render: r => <Pill tone={ageTone(r.days)}>{r.days} days</Pill> },
    { key: 'amount', header: 'Amount', align: 'right', render: r => formatCurrency(r.amount) }
  ]

  return (
    <ReportPage
      eyebrow="Money · Credit sales"
      title="Receivables"
      subtitle="Who owes how much, and for how long — all unpaid credit bills, regardless of date"
      report={report}
      onExport={() => exportCsv('receivables', customerColumns, data?.customers ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Total outstanding', value: formatCurrency(data.summary.totalOutstanding), tone: data.summary.totalOutstanding > 0 ? 'warn' : undefined },
              { label: 'Customers owing', value: data.summary.customerCount },
              { label: 'Open credit bills', value: data.summary.openBills },
              { label: 'Overdue 30+ days', value: formatCurrency(data.summary.over30), tone: data.summary.over30 > 0 ? 'bad' : undefined, highlight: data.summary.over30 > 0 ? 'warn' : undefined }
            ]}
          />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {BUCKET_LABELS.map((label, i) => (
              <div key={label} className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="mt-1 text-lg font-bold">{formatCurrency(data.summary.buckets[i] || 0)}</div>
              </div>
            ))}
          </div>
          <DataTable title="By customer" columns={customerColumns} rows={data.customers} rowKey={r => `${r.customer}|${r.phone}`} defaultSort="outstanding" csvName="receivables" empty="No outstanding credit. All settled." />
          <DataTable title="Open bills" columns={billColumns} rows={data.bills} rowKey={r => r._id} defaultSort="days" empty="No open credit bills." />
        </>
      )}
    </ReportPage>
  )
}
